import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { EditIcon, DeleteIcon, ActivateIcon, DeactivateIcon, DownloadIcon, AddIcon, CopyIcon } from '../components/Icons';
import { FaUpload } from 'react-icons/fa';
import RichTextEditor from '../components/RichTextEditor';
import './AdminProducts.css';

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

function AdminProducts() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { success, error: showError } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-products', debouncedSearch, page, limit, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
        sort: sortBy,
        order: sortOrder,
      });
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      const res = await apiClient.get(`/admin/products?${params.toString()}`);
      return res.data;
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-products']);
      setSelectedIds([]);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.post(`/admin/products/${id}/duplicate`);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['admin-products']);
      success(data?.message || 'Produktas nukopijuotas');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Klaida kopijuojant produktą');
    },
  });

  const handleDuplicate = (id) => {
    duplicateMutation.mutate(id);
  };

  const bulkActionMutation = useMutation({
    mutationFn: async ({ action }) => {
      if (!selectedIds || selectedIds.length === 0) {
        throw new Error('Nepasirinkta jokių produktų');
      }
      return apiClient.post('/products/bulk', {
        ids: selectedIds,
        action,
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['admin-products']);
      setSelectedIds([]);
      setSelectAll(false);
      success(response.data?.message || 'Veiksmas sėkmingas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida atliekant veiksmą');
    },
  });

  const handleDelete = (id, name) => {
    setConfirmModal({
      isOpen: true,
      title: 'Pašalinti produktą',
      message: `Ar tikrai norite pašalinti produktą "${name}"?`,
      type: 'danger',
      confirmText: 'Pašalinti',
      onConfirm: () => {
        deleteProductMutation.mutate(id);
        setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
      },
      onCancel: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })
    });
  };

  const handleBulkAction = (action) => {
    if (selectedIds.length === 0) {
      showError('Pasirinkite bent vieną produktą');
      return;
    }

    const actionText = {
      delete: 'pašalinti',
      activate: 'aktyvuoti',
      deactivate: 'deaktyvuoti',
    }[action];

    setConfirmModal({
      isOpen: true,
      title: 'Masinis veiksmas',
      message: `Ar tikrai norite ${actionText} ${selectedIds.length} produktą(-us)?`,
      type: action === 'delete' ? 'danger' : 'warning',
      confirmText: 'Patvirtinti',
      onConfirm: () => {
        bulkActionMutation.mutate({ action });
        setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
      },
      onCancel: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })
    });
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedIds(data?.products?.map(p => p.id) || []);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
      setSelectAll(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  // Update select all when products change
  useEffect(() => {
    if (data?.products && selectedIds.length === data.products.length && data.products.length > 0) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [data?.products, selectedIds.length]);

  if (isLoading) {
    return <div className="loading">Kraunama...</div>;
  }

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <span className="sort-icon">⇅</span>;
    return <span className="sort-icon">{sortOrder === 'ASC' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="admin-products-page container">
      <div className="admin-header">
        <h1>Produktų valdymas</h1>
        <div className="admin-actions">
          <input
            type="text"
            placeholder="Ieškoti produktų..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input search-input"
          />
          <button
            onClick={async () => {
              try {
                const response = await apiClient.get('/admin/import-export/export/csv', {
                  responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `products-export-${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
              } catch (error) {
                notification.error('Klaida eksportuojant produktus');
              }
            }}
            className="btn btn-secondary btn-sm"
            title="Eksportuoti į CSV"
          >
            <DownloadIcon />
            Eksportuoti
          </button>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            <FaUpload size={14} />
            Importuoti
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const formData = new FormData();
                formData.append('file', file);

                try {
                  const res = await apiClient.post('/admin/import-export/import/csv', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });
                  notification.info(res.data.message);
                  queryClient.invalidateQueries(['admin-products']);
                } catch (error) {
                  notification.error('Klaida importuojant produktus');
                }
                e.target.value = '';
              }}
            />
          </label>
          <button
            onClick={() => {
              setEditingProduct(null);
              setShowForm(true);
            }}
            className="btn btn-primary btn-sm"
          >
            <AddIcon />
            Pridėti produktą
          </button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="selected-count">Pasirinkta: {selectedIds.length}</span>
          <div className="bulk-buttons">
            <button
              onClick={() => handleBulkAction('activate')}
              className="btn btn-secondary btn-sm"
              disabled={bulkActionMutation.isLoading}
              title="Aktyvuoti"
            >
              <ActivateIcon />
              Aktyvuoti
            </button>
            <button
              onClick={() => handleBulkAction('deactivate')}
              className="btn btn-secondary btn-sm"
              disabled={bulkActionMutation.isLoading}
              title="Deaktyvuoti"
            >
              <DeactivateIcon />
              Deaktyvuoti
            </button>
            <button
              onClick={() => setShowBulkEdit(true)}
              className="btn btn-secondary btn-sm"
              title="Redaguoti"
            >
              <EditIcon />
              Redaguoti
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              className="btn btn-danger btn-sm"
              disabled={bulkActionMutation.isLoading}
              title="Pašalinti"
            >
              <DeleteIcon />
              Pašalinti
            </button>
          </div>
        </div>
      )}

      {showBulkEdit && (
        <BulkEditModal
          selectedIds={selectedIds}
          onClose={() => setShowBulkEdit(false)}
          onSuccess={() => {
            setShowBulkEdit(false);
            setSelectedIds([]);
            queryClient.invalidateQueries(['admin-products']);
          }}
        />
      )}

      {showForm && (
        <ProductForm
          product={editingProduct}
          onClose={() => {
            setShowForm(false);
            setEditingProduct(null);
          }}
          onSuccess={() => {
            setShowForm(false);
            setEditingProduct(null);
            queryClient.invalidateQueries(['admin-products']);
          }}
        />
      )}

      <div className="products-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th onClick={() => handleSort('id')} className="sortable">
                ID <SortIcon field="id" />
              </th>
              <th>Nuotrauka</th>
              <th onClick={() => handleSort('name')} className="sortable">
                Pavadinimas <SortIcon field="name" />
              </th>
              <th>SKU</th>
              <th onClick={() => handleSort('category_name')} className="sortable">
                Kategorija <SortIcon field="category_name" />
              </th>
              <th onClick={() => handleSort('price')} className="sortable">
                Kaina <SortIcon field="price" />
              </th>
              <th onClick={() => handleSort('stock_quantity')} className="sortable">
                Atsargos <SortIcon field="stock_quantity" />
              </th>
              <th onClick={() => handleSort('is_active')} className="sortable">
                Statusas <SortIcon field="is_active" />
              </th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {data?.products?.map((product) => {
              const isSelected = selectedIds.includes(product.id);
              return (
                <tr key={product.id} className={isSelected ? 'selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleSelectOne(product.id, e.target.checked)}
                    />
                  </td>
                  <td>{product.id}</td>
                  <td>
                    {product.primary_image ? (
                      <img src={product.primary_image} alt="" className="admin-product-thumb" />
                    ) : (
                      <span className="no-image">—</span>
                    )}
                  </td>
                  <td>
                    <Link to={`/products/${product.slug}`} target="_blank">
                      {product.name}
                    </Link>
                  </td>
                  <td>{product.sku || '—'}</td>
                  <td>{product.category_name || '—'}</td>
                  <td>{product.price ? parseFloat(product.price).toFixed(2) : '0.00'} €</td>
                  <td>{product.stock_quantity || 0}</td>
                  <td>
                    <span className={`status-badge ${product.is_active ? 'active' : 'inactive'}`}>
                      {product.is_active ? 'Aktyvus' : 'Neaktyvus'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={async () => {
                          try {
                            const res = await apiClient.get(`/admin/products/${product.id}`);
                            setEditingProduct(res.data);
                            setShowForm(true);
                          } catch (error) {
                            showError('Klaida gaunant produkto duomenis');
                          }
                        }}
                        className="btn-icon btn-icon-secondary"
                        title="Redaguoti"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => handleDuplicate(product.id)}
                        className="btn-icon btn-icon-secondary"
                        title="Kopijuoti prekę"
                        disabled={duplicateMutation.isLoading && duplicateMutation.variables === product.id}
                      >
                        <CopyIcon />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id, product.name)}
                        className="btn-icon btn-icon-danger"
                        title="Pašalinti"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {data?.products?.length === 0 && (
          <div className="empty-state">
            <p>Produktų nerasta</p>
            <button
              onClick={() => {
                setEditingProduct(null);
                setShowForm(true);
              }}
              className="btn btn-primary"
            >
              Pridėti pirmą produktą
            </button>
          </div>
        )}

        {data?.pagination && (
          <Pagination
            page={data.pagination.page}
            totalPages={data.pagination.pages}
            totalItems={data.pagination.total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
    </div>
  );
}

function ProductForm({ product, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  
  const [formData, setFormData] = useState({
    name: product?.name || '',
    slug: product?.slug || '',
    name_lt: product?.name_lt ?? product?.name ?? '',
    name_en: product?.name_en ?? '',
    name_de: product?.name_de ?? '',
    description_lt: product?.description_lt ?? product?.description ?? '',
    description_en: product?.description_en ?? '',
    description_de: product?.description_de ?? '',
    meta_title_lt: product?.meta_title_lt ?? product?.meta_title ?? '',
    meta_title_en: product?.meta_title_en ?? '',
    meta_title_de: product?.meta_title_de ?? '',
    meta_description_lt: product?.meta_description_lt ?? product?.meta_description ?? '',
    meta_description_en: product?.meta_description_en ?? '',
    meta_description_de: product?.meta_description_de ?? '',
    sku: product?.sku || '',
    barcode: product?.barcode || '',
    description: product?.description || '',
    price: product?.price ? (typeof product.price === 'number' ? product.price : parseFloat(product.price)) : '',
    compare_price: product?.compare_price ? (typeof product.compare_price === 'number' ? product.compare_price : parseFloat(product.compare_price)) : '',
    stock_quantity: product?.stock_quantity || 0,
    category_id: product?.category_id || '',
    weight: product?.weight || '',
    package_size: product?.package_size || '',
    length_cm: product?.length_cm || '',
    width_cm: product?.width_cm || '',
    height_cm: product?.height_cm || '',
    is_featured: product?.is_featured || false,
    is_active: product?.is_active !== undefined ? product.is_active : true,
    meta_title: product?.meta_title || '',
    meta_description: product?.meta_description || '',
    canonical_url: product?.canonical_url || '',
    schema_markup: product?.schema_markup || '',
  });
  const [activeLang, setActiveLang] = useState('lt');

  // Sinchronizuoti formą su produkto duomenimis (vertimai ir kt.) kai atidaromas redagavimui
  useEffect(() => {
    if (!product?.id) return;
    const p = product;
    const get = (key, fallback = '') => (p[key] != null && p[key] !== '') ? p[key] : (fallback ?? '');
    setFormData(prev => ({
      ...prev,
      name: get('name'),
      slug: get('slug'),
      name_lt: get('name_lt', p.name),
      name_en: get('name_en'),
      name_de: get('name_de'),
      description_lt: get('description_lt', p.description),
      description_en: get('description_en'),
      description_de: get('description_de'),
      description: get('description'),
      meta_title_lt: get('meta_title_lt', p.meta_title),
      meta_title_en: get('meta_title_en'),
      meta_title_de: get('meta_title_de'),
      meta_description_lt: get('meta_description_lt', p.meta_description),
      meta_description_en: get('meta_description_en'),
      meta_description_de: get('meta_description_de'),
      sku: get('sku'),
      barcode: get('barcode'),
      price: p.price != null ? (typeof p.price === 'number' ? p.price : parseFloat(p.price)) : '',
      compare_price: p.compare_price != null ? (typeof p.compare_price === 'number' ? p.compare_price : parseFloat(p.compare_price)) : '',
      stock_quantity: p.stock_quantity ?? 0,
      category_id: p.category_id || '',
      weight: p.weight ?? '',
      package_size: get('package_size'),
      length_cm: p.length_cm ?? '',
      width_cm: p.width_cm ?? '',
      height_cm: p.height_cm ?? '',
      is_featured: !!p.is_featured,
      is_active: p.is_active !== undefined ? !!p.is_active : true,
      meta_title: get('meta_title'),
      meta_description: get('meta_description'),
      canonical_url: get('canonical_url'),
      schema_markup: get('schema_markup'),
    }));
  }, [product]);

  const [images, setImages] = useState(() => {
    if (product?.images && product.images.length > 0) {
      return product.images.map(img => ({
        imageUrl: img.image_url || img.imageUrl || img,
        altText: img.alt_text || img.altText || '',
        id: img.id,
        sortOrder: img.sort_order || 0,
        isPrimary: img.is_primary || false
      }));
    }
    return [];
  });
  const [attributes, setAttributes] = useState(() => {
    if (product?.attributes && product.attributes.length > 0) {
      return product.attributes.map(attr => ({
        attribute_name_lt: attr.attribute_name_lt ?? attr.attribute_name ?? '',
        attribute_name_en: attr.attribute_name_en ?? '',
        attribute_name_de: attr.attribute_name_de ?? '',
        attribute_value_lt: attr.attribute_value_lt ?? attr.attribute_value ?? '',
        attribute_value_en: attr.attribute_value_en ?? '',
        attribute_value_de: attr.attribute_value_de ?? '',
        id: attr.id
      }));
    }
    return [];
  });

  // Atnaujinti nuotraukas ir atributus, kai produkto duomenys jau užkrauti (redagavimo atveju)
  useEffect(() => {
    if (!product?.id) return;
    if (product.images && product.images.length > 0) {
      setImages(product.images.map(img => ({
        imageUrl: img.image_url || img.imageUrl || img,
        altText: img.alt_text || img.altText || '',
        id: img.id,
        sortOrder: img.sort_order || 0,
        isPrimary: img.is_primary || false
      })));
    }
    if (product.attributes && product.attributes.length > 0) {
      setAttributes(product.attributes.map(attr => ({
        attribute_name_lt: attr.attribute_name_lt ?? attr.attribute_name ?? '',
        attribute_name_en: attr.attribute_name_en ?? '',
        attribute_name_de: attr.attribute_name_de ?? '',
        attribute_value_lt: attr.attribute_value_lt ?? attr.attribute_value ?? '',
        attribute_value_en: attr.attribute_value_en ?? '',
        attribute_value_de: attr.attribute_value_de ?? '',
        id: attr.id
      })));
    }
  }, [product?.id, product?.images, product?.attributes]);
  const [variants, setVariants] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tags, setTags] = useState([]);
  const [collections, setCollections] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [crossSells, setCrossSells] = useState([]);
  const [upsells, setUpsells] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [sales, setSales] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [allCollections, setAllCollections] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [productPickerType, setProductPickerType] = useState(null);
  const [productPickerSearch, setProductPickerSearch] = useState('');
  const [bundleQuantity, setBundleQuantity] = useState(1);
  const [bundleDiscountPercent, setBundleDiscountPercent] = useState(0);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await apiClient.get('/categories');
      return res.data;
    },
  });

  // Užkrauti produkto duomenis redaguojant (visos klaidos – tušti masyvai, jokio unhandled rejection)
  useEffect(() => {
    if (!product?.id) return;
    const load = async () => {
      const empty = [];
      try {
        const res = await apiClient.get(`/admin/variants/product/${product.id}`);
        setVariants(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setVariants(empty);
      }
      try {
        const res = await apiClient.get(`/admin/videos/product/${product.id}`);
        setVideos(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setVideos(empty);
      }
      try {
        const res = await apiClient.get(`/admin/tags/product/${product.id}`);
        setTags(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setTags(empty);
      }
      try {
        const res = await apiClient.get(`/admin/collections/product/${product.id}`);
        setCollections(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setCollections(empty);
      }
      try {
        const res = await apiClient.get(`/admin/product-relations/bundles/${product.id}`);
        setBundles(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setBundles(empty);
      }
      try {
        const res = await apiClient.get(`/admin/product-relations/cross-sells/${product.id}`);
        setCrossSells(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setCrossSells(empty);
      }
      try {
        const res = await apiClient.get(`/admin/product-relations/upsells/${product.id}`);
        setUpsells(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setUpsells(empty);
      }
      try {
        const res = await apiClient.get(`/admin/price-history/product/${product.id}`);
        setPriceHistory(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setPriceHistory(empty);
      }
      try {
        const res = await apiClient.get(`/admin/sales/product/${product.id}`);
        setSales(Array.isArray(res?.data) ? res.data : empty);
      } catch {
        setSales(empty);
      }
    };
    load();
  }, [product?.id]);

  // Užkrauti visas žymas ir kolekcijas
  useEffect(() => {
    apiClient.get('/admin/tags')
      .then(res => setAllTags(res.data ?? []))
      .catch(() => setAllTags([]));
    apiClient.get('/admin/collections')
      .then(res => setAllCollections(res.data ?? []))
      .catch(() => setAllCollections([]));
  }, []);

  // Produktų sąrašas pasirinkimo modalui (Komplektai / Cross-sell / Upsell)
  const { data: pickerProductsData } = useQuery({
    queryKey: ['admin-products-picker', productPickerSearch],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50', page: '1' });
      if (productPickerSearch) params.set('search', productPickerSearch);
      const res = await apiClient.get(`/admin/products?${params.toString()}`);
      return res.data;
    },
    enabled: !!productPickerType,
  });
  const pickerProducts = (pickerProductsData?.products || []).filter(p => {
    if (p.id === product?.id) return false;
    if (productPickerType === 'bundle' && bundles.some(b => b.included_product_id === p.id)) return false;
    if (productPickerType === 'cross-sell' && crossSells.some(c => c.cross_sell_product_id === p.id)) return false;
    if (productPickerType === 'upsell' && upsells.some(u => u.upsell_product_id === p.id)) return false;
    return true;
  });

  const handleAddRelation = async (selectedProduct) => {
    if (!product?.id) return;
    try {
      if (productPickerType === 'bundle') {
        await apiClient.post('/admin/product-relations/bundles', {
          bundle_product_id: product.id,
          included_product_id: selectedProduct.id,
          quantity: bundleQuantity || 1,
          discount_percent: bundleDiscountPercent || 0,
        });
        const res = await apiClient.get(`/admin/product-relations/bundles/${product.id}`);
        setBundles(res.data || []);
        notification.success('Produktas pridėtas į komplektą');
      } else if (productPickerType === 'cross-sell') {
        await apiClient.post('/admin/product-relations/cross-sells', {
          product_id: product.id,
          cross_sell_product_id: selectedProduct.id,
        });
        const res = await apiClient.get(`/admin/product-relations/cross-sells/${product.id}`);
        setCrossSells(res.data || []);
        notification.success('Cross-sell produktas pridėtas');
      } else if (productPickerType === 'upsell') {
        await apiClient.post('/admin/product-relations/upsells', {
          product_id: product.id,
          upsell_product_id: selectedProduct.id,
        });
        const res = await apiClient.get(`/admin/product-relations/upsells/${product.id}`);
        setUpsells(res.data || []);
        notification.success('Upsell produktas pridėtas');
      }
      setProductPickerType(null);
      setProductPickerSearch('');
    } catch (err) {
      notification.error(err.response?.data?.error || 'Klaida pridedant');
    }
  };

  const saveProductMutation = useMutation({
    mutationFn: async (data) => {
      if (product) {
        return apiClient.put(`/products/${product.id}`, data);
      } else {
        return apiClient.post('/products', data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-products']);
      onSuccess();
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida išsaugant produktą');
    },
  });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));

      const res = await apiClient.post('/upload/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const newImages = res.data.images.map((img, idx) => ({
        imageUrl: img.imageUrl,
        altText: '',
        isPrimary: images.length === 0 && idx === 0,
        sortOrder: images.length + idx
      }));

      setImages([...images, ...newImages]);
    } catch (error) {
      notification.error(error.response?.data?.error || 'Klaida įkeliant nuotraukas');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleMoveImage = (index, direction) => {
    const newImages = [...images];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newImages.length) {
      [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      setImages(newImages);
    }
  };

  const handleReplaceImage = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('images', file);

      const res = await apiClient.post('/upload/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data.images && res.data.images.length > 0) {
        const newImages = [...images];
        newImages[index] = {
          ...newImages[index],
          imageUrl: res.data.images[0].imageUrl,
        };
        setImages(newImages);
      }
    } catch (error) {
      notification.error(error.response?.data?.error || 'Klaida keičiant nuotrauką');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleSetPrimary = (index) => {
    const newImages = images.map((img, i) => ({
      ...img,
      isPrimary: i === index
    }));
    setImages(newImages);
  };

  const handleAddAttribute = () => {
    setAttributes([...attributes, {
      attribute_name_lt: '', attribute_name_en: '', attribute_name_de: '',
      attribute_value_lt: '', attribute_value_en: '', attribute_value_de: ''
    }]);
  };

  const handleRemoveAttribute = (index) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nameLt = (formData.name_lt || '').trim();
    const nameEn = (formData.name_en || '').trim();
    const nameDe = (formData.name_de || '').trim();
    if (!nameLt && !nameEn && !nameDe) {
      notification.warning('Pavadinimas privalomas bent viena kalba (LT, EN arba DE)');
      return;
    }

    const firstName = nameLt || nameEn || nameDe;
    let slug = formData.slug;
    if (!slug && firstName) {
      slug = firstName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }

    const productData = {
      name: firstName,
      name_lt: nameLt || null,
      name_en: nameEn || null,
      name_de: nameDe || null,
      description_lt: (formData.description_lt || '').trim() || null,
      description_en: (formData.description_en || '').trim() || null,
      description_de: (formData.description_de || '').trim() || null,
      meta_title_lt: (formData.meta_title_lt || '').trim() || null,
      meta_title_en: (formData.meta_title_en || '').trim() || null,
      meta_title_de: (formData.meta_title_de || '').trim() || null,
      meta_description_lt: (formData.meta_description_lt || '').trim() || null,
      meta_description_en: (formData.meta_description_en || '').trim() || null,
      meta_description_de: (formData.meta_description_de || '').trim() || null,
      description: (formData.description_lt || formData.description_en || formData.description_de || '').trim() || null,
      meta_title: (formData.meta_title_lt || formData.meta_title_en || formData.meta_title_de || '').trim() || null,
      meta_description: (formData.meta_description_lt || formData.meta_description_en || formData.meta_description_de || '').trim() || null,
      slug,
      sku: formData.sku || null,
      barcode: formData.barcode || null,
      price: parseFloat(formData.price) || 0,
      compare_price: formData.compare_price ? parseFloat(formData.compare_price) : null,
      stock_quantity: parseInt(formData.stock_quantity) || 0,
      category_id: formData.category_id || null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      package_size: (formData.package_size && formData.package_size.trim()) ? formData.package_size.trim() : null,
      length_cm: formData.length_cm ? parseFloat(formData.length_cm) : null,
      width_cm: formData.width_cm ? parseFloat(formData.width_cm) : null,
      height_cm: formData.height_cm ? parseFloat(formData.height_cm) : null,
      is_featured: formData.is_featured || false,
      is_active: formData.is_active !== undefined ? formData.is_active : true,
      canonical_url: formData.canonical_url || null,
      schema_markup: formData.schema_markup || null,
    };

    // Pridėti nuotraukas jei yra
    if (images && images.length > 0) {
      productData.images = images.map((img, index) => ({
        imageUrl: img.imageUrl || img,
        altText: img.altText || formData.name,
        isPrimary: index === 0 || img.isPrimary,
        sortOrder: index
      }));
    }

    // Pridėti atributus jei yra (bent viena kalba pavadinimui ir reikšmei)
    const validAttributes = attributes.filter(attr => {
      const hasName = (attr.attribute_name_lt || '').trim() || (attr.attribute_name_en || '').trim() || (attr.attribute_name_de || '').trim();
      const hasValue = (attr.attribute_value_lt || '').trim() || (attr.attribute_value_en || '').trim() || (attr.attribute_value_de || '').trim();
      return hasName && hasValue;
    });
    if (validAttributes.length > 0) {
      productData.attributes = validAttributes.map(attr => ({
        attribute_name_lt: (attr.attribute_name_lt || '').trim() || null,
        attribute_name_en: (attr.attribute_name_en || '').trim() || null,
        attribute_name_de: (attr.attribute_name_de || '').trim() || null,
        attribute_value_lt: (attr.attribute_value_lt || '').trim() || null,
        attribute_value_en: (attr.attribute_value_en || '').trim() || null,
        attribute_value_de: (attr.attribute_value_de || '').trim() || null
      }));
    }

    saveProductMutation.mutate(productData, {
      onSuccess: async (response) => {
        const productId = product?.id || response.data.id;
        
        // Išsaugoti variantus
        if (variants.length > 0 && productId) {
          for (const variant of variants) {
            if (variant.id) {
              await apiClient.put(`/admin/variants/${variant.id}`, { ...variant, product_id: productId });
            } else {
              await apiClient.post('/admin/variants', { ...variant, product_id: productId });
            }
          }
        }

        // Išsaugoti video
        if (videos.length > 0 && productId) {
          for (const video of videos) {
            if (video.id) {
              await apiClient.put(`/admin/videos/${video.id}`, { ...video, product_id: productId });
            } else {
              await apiClient.post('/admin/videos', { ...video, product_id: productId });
            }
          }
        }

        // Išsaugoti akcijas
        if (sales.length > 0 && productId) {
          for (const sale of sales) {
            if (sale.id) {
              await apiClient.put(`/admin/sales/${sale.id}`, { ...sale, product_id: productId });
            } else {
              await apiClient.post('/admin/sales', { ...sale, product_id: productId });
            }
          }
        }
      }
    });
  };

  return (
    <div className="product-form-overlay" onClick={onClose}>
      <div className="product-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <h2>{product ? 'Redaguoti produktą' : 'Pridėti naują produktą'}</h2>
          <button onClick={onClose} className="btn-close">×</button>
        </div>

        <form onSubmit={handleSubmit} className="product-form">
          {/* Tab Navigation */}
          <div className="form-tabs">
            <button type="button" className={activeTab === 'basic' ? 'active' : ''} onClick={() => setActiveTab('basic')}>
              Pagrindinė
            </button>
            <button type="button" className={activeTab === 'media' ? 'active' : ''} onClick={() => setActiveTab('media')}>
              Media
            </button>
            <button type="button" className={activeTab === 'variants' ? 'active' : ''} onClick={() => setActiveTab('variants')}>
              Variantai
            </button>
            <button type="button" className={activeTab === 'relations' ? 'active' : ''} onClick={() => setActiveTab('relations')}>
              Ryšiai
            </button>
            <button type="button" className={activeTab === 'seo' ? 'active' : ''} onClick={() => setActiveTab('seo')}>
              SEO
            </button>
            <button type="button" className={activeTab === 'pricing' ? 'active' : ''} onClick={() => setActiveTab('pricing')}>
              Kainodara
            </button>
          </div>

          {/* Basic Tab */}
          {activeTab === 'basic' && (
            <>
          <div className="form-section">
            <h3>Pagrindinė informacija (3 kalbos)</h3>
            <div className="locale-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {LOCALES.map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  className={activeLang === code ? 'active' : ''}
                  onClick={() => setActiveLang(code)}
                  style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', background: activeLang === code ? '#eee' : 'transparent' }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="form-group">
              <label>Pavadinimas * ({LOCALES.find(l => l.code === activeLang)?.label})</label>
              <input
                type="text"
                value={formData[`name_${activeLang}`] || formData.name_lt || formData.name_en || formData.name_de || ''}
                onChange={(e) => setFormData({ ...formData, [`name_${activeLang}`]: e.target.value })}
                className="input"
                placeholder="Bent viena kalba privaloma"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>URL slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="auto-generuojamas"
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>SKU</label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  className="input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Brūkšninis kodas (Barcode)</label>
              <input
                type="text"
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                className="input"
                placeholder="EAN, UPC, ISBN, etc."
              />
            </div>

            <div className="form-group">
              <label>Aprašymas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
              <RichTextEditor
                value={formData[`description_${activeLang}`] || formData.description_lt || formData.description_en || formData.description_de || ''}
                onChange={(html) => setFormData({ ...formData, [`description_${activeLang}`]: html })}
                placeholder="Aprašymas..."
                minHeight={140}
              />
            </div>

            <div className="form-section">
              <h3>Atributai (3 kalbos)</h3>
              <p className="form-help-text">Pvz. Spalva, Dydis – įveskite bent viena kalba</p>
              <div className="locale-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                {LOCALES.map(({ code, label }) => (
                  <button key={code} type="button" className={activeLang === code ? 'active' : ''} onClick={() => setActiveLang(code)} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', background: activeLang === code ? '#eee' : 'transparent' }}>{label}</button>
                ))}
              </div>
              {attributes.map((attr, index) => (
                <div key={attr.id || index} className="form-row" style={{ marginBottom: '8px', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Pavadinimas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="pvz. Spalva"
                      value={attr[`attribute_name_${activeLang}`] || attr.attribute_name_lt || attr.attribute_name_en || attr.attribute_name_de || ''}
                      onChange={(e) => {
                        const next = [...attributes];
                        next[index] = { ...next[index], [`attribute_name_${activeLang}`]: e.target.value };
                        setAttributes(next);
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Reikšmė ({LOCALES.find(l => l.code === activeLang)?.label})</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="pvz. Raudona"
                      value={attr[`attribute_value_${activeLang}`] || attr.attribute_value_lt || attr.attribute_value_en || attr.attribute_value_de || ''}
                      onChange={(e) => {
                        const next = [...attributes];
                        next[index] = { ...next[index], [`attribute_value_${activeLang}`]: e.target.value };
                        setAttributes(next);
                      }}
                    />
                  </div>
                  <button type="button" onClick={() => handleRemoveAttribute(index)} className="btn-icon-small">×</button>
                </div>
              ))}
              <button type="button" onClick={handleAddAttribute} className="btn btn-secondary">+ Pridėti atributą</button>
            </div>
          </div>

          <div className="form-section">
            <h3>Kaina ir atsargos</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Kaina (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>Palyginimo kaina (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.compare_price}
                  onChange={(e) => setFormData({ ...formData, compare_price: e.target.value })}
                  className="input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Atsargų kiekis</label>
                <input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>Kategorija</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="input"
                >
                  <option value="">Pasirinkite kategoriją</option>
                  {categories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Matmenys ir svoris</h3>
            <div className="form-group">
              <label>Siuntos dydis</label>
              <select
                value={formData.package_size || ''}
                onChange={(e) => setFormData({ ...formData, package_size: e.target.value })}
                className="input"
              >
                <option value="">Nenurodyta</option>
                <option value="xs">XS</option>
                <option value="s">S</option>
                <option value="m">M</option>
                <option value="l">L</option>
                <option value="xl">XL</option>
              </select>
              <small style={{ color: '#666', fontSize: '12px' }}>Pristatymo taisyklėse naudojami tie patys dydžiai</small>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Svoris (kg)</label>
                <input
                  type="number"
                  step="0.001"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="input"
                  placeholder="kg"
                />
              </div>
              <div className="form-group">
                <label>Ilgis (cm)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.length_cm}
                  onChange={(e) => setFormData({ ...formData, length_cm: e.target.value })}
                  className="input"
                  placeholder="cm"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Plotis (cm)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.width_cm}
                  onChange={(e) => setFormData({ ...formData, width_cm: e.target.value })}
                  className="input"
                  placeholder="cm"
                />
              </div>
              <div className="form-group">
                <label>Aukštis (cm)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.height_cm}
                  onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })}
                  className="input"
                  placeholder="cm"
                />
              </div>
            </div>
          </div>

          </>
          )}

          {/* Media Tab */}
          {activeTab === 'media' && (
            <>
          <div className="form-section">
            <h3>Nuotraukos</h3>
            <div className="image-upload-area">
              <input
                type="file"
                id="image-upload"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              <label htmlFor="image-upload" className="upload-button">
                {uploading ? 'Įkeliama...' : '+ Pridėti nuotraukas'}
              </label>
            </div>

            <div className="images-grid">
              {images.map((image, index) => (
                <div key={index} className="image-preview">
                  <img 
                    src={image.imageUrl} 
                    alt={image.altText || `Nuotrauka ${index + 1}`}
                    onClick={() => setPreviewImage(image.imageUrl)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="image-actions">
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="remove-image"
                      title="Pašalinti"
                    >
                      ×
                    </button>
                    <div className="image-controls">
                      <button
                        type="button"
                        onClick={() => handleMoveImage(index, 'up')}
                        disabled={index === 0}
                        className="image-control-btn"
                        title="Aukštyn"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveImage(index, 'down')}
                        disabled={index === images.length - 1}
                        className="image-control-btn"
                        title="Žemyn"
                      >
                        ↓
                      </button>
                      <label className="image-control-btn" title="Keisti nuotrauką">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleReplaceImage(index, e)}
                          style={{ display: 'none' }}
                        />
                        🔄
                      </label>
                      <button
                        type="button"
                        onClick={() => handleSetPrimary(index)}
                        className={`image-control-btn ${image.isPrimary || index === 0 ? 'active' : ''}`}
                        title="Nustatyti kaip pagrindinę"
                      >
                        ⭐
                      </button>
                    </div>
                  </div>
                  {(image.isPrimary || index === 0) && <span className="primary-badge">Pagrindinė</span>}
                  <span className="image-number">{index + 1}</span>
                </div>
              ))}
            </div>
            
            {previewImage && (
              <div className="image-preview-modal" onClick={() => setPreviewImage(null)}>
                <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
                  <button 
                    className="preview-close"
                    onClick={() => setPreviewImage(null)}
                  >
                    ×
                  </button>
                  <img src={previewImage} alt="Peržiūra" />
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Video</h3>
            <div className="videos-list">
              {videos.map((video, index) => (
                <div key={video.id || index} className="video-item">
                  <input
                    type="text"
                    placeholder="Video URL"
                    value={video.video_url || ''}
                    onChange={(e) => {
                      const newVideos = [...videos];
                      newVideos[index].video_url = e.target.value;
                      setVideos(newVideos);
                    }}
                    className="input"
                  />
                  <select
                    value={video.video_type || 'direct'}
                    onChange={(e) => {
                      const newVideos = [...videos];
                      newVideos[index].video_type = e.target.value;
                      setVideos(newVideos);
                    }}
                    className="input"
                  >
                    <option value="direct">Tiesioginis</option>
                    <option value="youtube">YouTube</option>
                    <option value="vimeo">Vimeo</option>
                    <option value="embed">Embed</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setVideos(videos.filter((_, i) => i !== index))}
                    className="btn-icon-small"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setVideos([...videos, { video_url: '', video_type: 'direct' }])}
                className="btn btn-secondary"
              >
                + Pridėti video
              </button>
            </div>
          </div>
          </>
          )}

          {/* Variants Tab */}
          {activeTab === 'variants' && (
            <div className="form-section">
              <h3>Produkto variantai (3 kalbos)</h3>
              <p className="form-help-text">Sukurkite variantus su skirtingomis spalvomis, dydžiais ar kitais atributais</p>
              <div className="locale-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                {LOCALES.map(({ code, label }) => (
                  <button key={code} type="button" className={activeLang === code ? 'active' : ''} onClick={() => setActiveLang(code)} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', background: activeLang === code ? '#eee' : 'transparent' }}>{label}</button>
                ))}
              </div>
              <div className="variants-list">
                {variants.map((variant, index) => (
                  <div key={variant.id || index} className="variant-item">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Pavadinimas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
                        <input
                          type="text"
                          value={variant[`name_${activeLang}`] ?? variant.name ?? ''}
                          onChange={(e) => {
                            const newVariants = [...variants];
                            newVariants[index] = { ...newVariants[index], [`name_${activeLang}`]: e.target.value };
                            setVariants(newVariants);
                          }}
                          className="input"
                          placeholder="pvz., Raudona, L"
                        />
                      </div>
                      <div className="form-group">
                        <label>SKU</label>
                        <input
                          type="text"
                          value={variant.sku || ''}
                          onChange={(e) => {
                            const newVariants = [...variants];
                            newVariants[index].sku = e.target.value;
                            setVariants(newVariants);
                          }}
                          className="input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Kaina</label>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.price || ''}
                          onChange={(e) => {
                            const newVariants = [...variants];
                            newVariants[index].price = e.target.value;
                            setVariants(newVariants);
                          }}
                          className="input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Atsargos</label>
                        <input
                          type="number"
                          value={variant.stock_quantity || 0}
                          onChange={(e) => {
                            const newVariants = [...variants];
                            newVariants[index].stock_quantity = e.target.value;
                            setVariants(newVariants);
                          }}
                          className="input"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setVariants(variants.filter((_, i) => i !== index))}
                        className="btn-icon-small"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setVariants([...variants, { name_lt: '', name_en: '', name_de: '', sku: '', price: '', stock_quantity: 0 }])}
                  className="btn btn-secondary"
                >
                  + Pridėti variantą
                </button>
              </div>
            </div>
          )}

          {/* Relations Tab */}
          {activeTab === 'relations' && (
            <>
            <div className="form-section">
              <h3>Žymos</h3>
              <div className="tags-selector">
                {allTags.map(tag => (
                  <label key={tag.id} className="tag-checkbox">
                    <input
                      type="checkbox"
                      checked={tags.some(t => t.id === tag.id)}
                      onChange={async (e) => {
                        if (e.target.checked) {
                          if (product?.id) {
                            try {
                              await apiClient.post(`/admin/tags/product/${product.id}`, { tag_id: tag.id });
                              setTags([...tags, tag]);
                            } catch (err) {
                            }
                          } else {
                            setTags([...tags, tag]);
                          }
                        } else {
                          if (product?.id) {
                            try {
                              await apiClient.delete(`/admin/tags/product/${product.id}/${tag.id}`);
                              setTags(tags.filter(t => t.id !== tag.id));
                            } catch (err) {
                            }
                          } else {
                            setTags(tags.filter(t => t.id !== tag.id));
                          }
                        }
                      }}
                    />
                    {tag.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-section">
              <h3>Kolekcijos</h3>
              <div className="collections-selector">
                {allCollections.map(collection => (
                  <label key={collection.id} className="collection-checkbox">
                    <input
                      type="checkbox"
                      checked={collections.some(c => c.id === collection.id)}
                      onChange={async (e) => {
                        if (e.target.checked) {
                          if (product?.id) {
                            try {
                              await apiClient.post(`/admin/collections/product/${product.id}`, { collection_id: collection.id });
                              setCollections([...collections, collection]);
                            } catch (err) {
                            }
                          } else {
                            setCollections([...collections, collection]);
                          }
                        } else {
                          if (product?.id) {
                            try {
                              await apiClient.delete(`/admin/collections/product/${product.id}/${collection.id}`);
                              setCollections(collections.filter(c => c.id !== collection.id));
                            } catch (err) {
                            }
                          } else {
                            setCollections(collections.filter(c => c.id !== collection.id));
                          }
                        }
                      }}
                    />
                    {collection.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-section">
              <h3>Komplektai (Bundles)</h3>
              <p className="form-help-text">Produktai, kurie įtraukiami į šį komplektą</p>
              <div className="bundles-list">
                {bundles.map((bundle, index) => (
                  <div key={bundle.id || index} className="relation-item">
                    <span>{bundle.product_name} (SKU: {bundle.product_sku})</span>
                    <span>Kiekis: {bundle.quantity}</span>
                    <span>Nuolaida: {bundle.discount_percent}%</span>
                    <button
                      type="button"
                      onClick={async () => {
                        if (bundle.id && product?.id) {
                          try {
                            await apiClient.delete(`/admin/product-relations/bundles/${bundle.id}`);
                            setBundles(bundles.filter((_, i) => i !== index));
                            notification.success('Produktas pašalintas iš komplekto');
                          } catch (err) {
                            notification.error(err.response?.data?.error || 'Nepavyko pašalinti');
                          }
                        } else {
                          setBundles(bundles.filter((_, i) => i !== index));
                        }
                      }}
                      className="btn-icon-small btn-remove-relation"
                      title="Pašalinti iš komplekto"
                      aria-label="Pašalinti iš komplekto"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    if (!product?.id) {
                      notification.warning('Pirmiau išsaugokite produktą, tada galėsite pridėti produktus į komplektą');
                      return;
                    }
                    setProductPickerType('bundle');
                    setProductPickerSearch('');
                  }}
                  className="btn btn-secondary"
                >
                  + Pridėti produktą į komplektą
                </button>
              </div>
            </div>

            <div className="form-section">
              <h3>Cross-sell produktai</h3>
              <p className="form-help-text">Produktai, kurie rodomi kartu su šiuo produktu</p>
              <div className="cross-sells-list">
                {crossSells.map((cs, index) => (
                  <div key={cs.id || index} className="relation-item">
                    <span>{cs.product_name}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        if (cs.id && product?.id) {
                          try {
                            await apiClient.delete(`/admin/product-relations/cross-sells/${cs.id}`);
                            setCrossSells(crossSells.filter((_, i) => i !== index));
                            notification.success('Cross-sell ryšys pašalintas');
                          } catch (err) {
                            notification.error(err.response?.data?.error || 'Nepavyko pašalinti');
                          }
                        } else {
                          setCrossSells(crossSells.filter((_, i) => i !== index));
                        }
                      }}
                      className="btn-icon-small btn-remove-relation"
                      title="Pašalinti ryšį"
                      aria-label="Pašalinti ryšį"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    if (!product?.id) {
                      notification.warning('Pirmiau išsaugokite produktą, tada galėsite pridėti cross-sell produktus');
                      return;
                    }
                    setProductPickerType('cross-sell');
                    setProductPickerSearch('');
                  }}
                  className="btn btn-secondary"
                >
                  + Pridėti cross-sell produktą
                </button>
              </div>
            </div>

            <div className="form-section">
              <h3>Upsell produktai</h3>
              <p className="form-help-text">Produktai, kurie siūlomi kaip geresnė alternatyva</p>
              <div className="upsells-list">
                {upsells.map((upsell, index) => (
                  <div key={upsell.id || index} className="relation-item">
                    <span>{upsell.product_name}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        if (upsell.id && product?.id) {
                          try {
                            await apiClient.delete(`/admin/product-relations/upsells/${upsell.id}`);
                            setUpsells(upsells.filter((_, i) => i !== index));
                            notification.success('Upsell ryšys pašalintas');
                          } catch (err) {
                            notification.error(err.response?.data?.error || 'Nepavyko pašalinti');
                          }
                        } else {
                          setUpsells(upsells.filter((_, i) => i !== index));
                        }
                      }}
                      className="btn-icon-small btn-remove-relation"
                      title="Pašalinti ryšį"
                      aria-label="Pašalinti ryšį"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    if (!product?.id) {
                      notification.warning('Pirmiau išsaugokite produktą, tada galėsite pridėti upsell produktus');
                      return;
                    }
                    setProductPickerType('upsell');
                    setProductPickerSearch('');
                  }}
                  className="btn btn-secondary"
                >
                  + Pridėti upsell produktą
                </button>
              </div>
            </div>
            </>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <>
            <div className="form-section">
              <h3>SEO nustatymai (3 kalbos)</h3>
              <div className="locale-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                {LOCALES.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    className={activeLang === code ? 'active' : ''}
                    onClick={() => setActiveLang(code)}
                    style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', background: activeLang === code ? '#eee' : 'transparent' }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="form-group">
                <label>Meta Title ({LOCALES.find(l => l.code === activeLang)?.label})</label>
                <input
                  type="text"
                  value={formData[`meta_title_${activeLang}`] || formData.meta_title_lt || formData.meta_title_en || formData.meta_title_de || ''}
                  onChange={(e) => setFormData({ ...formData, [`meta_title_${activeLang}`]: e.target.value })}
                  className="input"
                  placeholder="SEO title (50-60 simbolių)"
                  maxLength={60}
                />
                <small className="form-help-text">{(formData[`meta_title_${activeLang}`] || formData.meta_title_lt || formData.meta_title_en || formData.meta_title_de || '').length}/60 simbolių</small>
              </div>
              <div className="form-group">
                <label>Meta Description ({LOCALES.find(l => l.code === activeLang)?.label})</label>
                <textarea
                  value={formData[`meta_description_${activeLang}`] || formData.meta_description_lt || formData.meta_description_en || formData.meta_description_de || ''}
                  onChange={(e) => setFormData({ ...formData, [`meta_description_${activeLang}`]: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="SEO aprašymas (150-160 simbolių)"
                  maxLength={160}
                />
                <small className="form-help-text">{(formData[`meta_description_${activeLang}`] || formData.meta_description_lt || formData.meta_description_en || formData.meta_description_de || '').length}/160 simbolių</small>
              </div>
              <div className="form-group">
                <label>Canonical URL</label>
                <input
                  type="url"
                  value={formData.canonical_url}
                  onChange={(e) => setFormData({ ...formData, canonical_url: e.target.value })}
                  className="input"
                  placeholder="https://example.com/product"
                />
              </div>
              <div className="form-group">
                <label>Schema Markup (JSON-LD)</label>
                <textarea
                  value={formData.schema_markup}
                  onChange={(e) => setFormData({ ...formData, schema_markup: e.target.value })}
                  className="input"
                  rows="10"
                  placeholder='{"@context": "https://schema.org", "@type": "Product", ...}'
                />
                <small className="form-help-text">JSON-LD formatas struktūruotoms duomenims</small>
              </div>
            </div>
            </>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <>
            <div className="form-section">
              <h3>Akcijos (3 kalbos)</h3>
              <div className="locale-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                {LOCALES.map(({ code, label }) => (
                  <button key={code} type="button" className={activeLang === code ? 'active' : ''} onClick={() => setActiveLang(code)} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', background: activeLang === code ? '#eee' : 'transparent' }}>{label}</button>
                ))}
              </div>
              <div className="sales-list">
                {sales.map((sale, index) => (
                  <div key={sale.id || index} className="sale-item">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Akcijos pavadinimas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
                        <input
                          type="text"
                          value={sale[`name_${activeLang}`] ?? sale.name_lt ?? sale.name_en ?? sale.name_de ?? sale.name ?? ''}
                          onChange={(e) => {
                            const newSales = [...sales];
                            newSales[index] = { ...newSales[index], [`name_${activeLang}`]: e.target.value };
                            setSales(newSales);
                          }}
                          className="input"
                          placeholder="pvz. Black Friday"
                        />
                      </div>
                      <div className="form-group">
                        <label>Akcijos kaina</label>
                        <input
                          type="number"
                          step="0.01"
                          value={sale.sale_price || ''}
                          onChange={(e) => {
                            const newSales = [...sales];
                            newSales[index].sale_price = e.target.value;
                            setSales(newSales);
                          }}
                          className="input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Pradžios data</label>
                        <input
                          type="datetime-local"
                          value={sale.start_date ? new Date(sale.start_date).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const newSales = [...sales];
                            newSales[index].start_date = e.target.value;
                            setSales(newSales);
                          }}
                          className="input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Pabaigos data</label>
                        <input
                          type="datetime-local"
                          value={sale.end_date ? new Date(sale.end_date).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const newSales = [...sales];
                            newSales[index].end_date = e.target.value;
                            setSales(newSales);
                          }}
                          className="input"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setSales(sales.filter((_, i) => i !== index))}
                        className="btn-icon-small"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setSales([...sales, { name_lt: '', name_en: '', name_de: '', sale_price: '', start_date: '', end_date: '', is_active: true }])}
                  className="btn btn-secondary"
                >
                  + Pridėti akciją
                </button>
              </div>
            </div>

            <div className="form-section">
              <h3>Kainos istorija</h3>
              {priceHistory.length > 0 ? (
                <div className="price-history-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Kaina</th>
                        <th>Palyginimo kaina</th>
                        <th>Pakeitė</th>
                        <th>Priežastis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceHistory.map((entry, index) => (
                        <tr key={entry.id || index}>
                          <td>{new Date(entry.created_at).toLocaleString('lt-LT')}</td>
                          <td>{parseFloat(entry.price).toFixed(2)} €</td>
                          <td>{entry.compare_price ? parseFloat(entry.compare_price).toFixed(2) + ' €' : '—'}</td>
                          <td>{entry.changed_by_email || '—'}</td>
                          <td>{entry.change_reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="form-help-text">Kainos istorija bus rodoma po pirmojo išsaugojimo</p>
              )}
            </div>
            </>
          )}

          {/* Basic Tab - Settings */}
          {activeTab === 'basic' && (
            <>
          <div className="form-section">
            <h3>Nustatymai</h3>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Aktyvus
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                />
                Rekomenduojamas
              </label>
            </div>
          </div>
          </>
          )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Atšaukti
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saveProductMutation.isLoading}
            >
              {saveProductMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
            </button>
          </div>
        </form>
      </div>

      {/* Produkto pasirinkimo modalas (Komplektai / Cross-sell / Upsell) */}
      {productPickerType && (
        <div className="product-picker-overlay" onClick={() => setProductPickerType(null)}>
          <div className="product-picker-modal" onClick={e => e.stopPropagation()}>
            <div className="product-picker-header">
              <h3>
                {productPickerType === 'bundle' && 'Pridėti produktą į komplektą'}
                {productPickerType === 'cross-sell' && 'Pridėti cross-sell produktą'}
                {productPickerType === 'upsell' && 'Pridėti upsell produktą'}
              </h3>
              <button type="button" className="btn-close" onClick={() => setProductPickerType(null)}>×</button>
            </div>
            {productPickerType === 'bundle' && (
              <div className="product-picker-bundle-opts">
                <label>
                  Kiekis:
                  <input
                    type="number"
                    min="1"
                    value={bundleQuantity}
                    onChange={e => setBundleQuantity(parseInt(e.target.value, 10) || 1)}
                    className="input"
                  />
                </label>
                <label>
                  Nuolaida %:
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={bundleDiscountPercent}
                    onChange={e => setBundleDiscountPercent(parseFloat(e.target.value) || 0)}
                    className="input"
                  />
                </label>
              </div>
            )}
            <input
              type="text"
              placeholder="Ieškoti produktų..."
              value={productPickerSearch}
              onChange={e => setProductPickerSearch(e.target.value)}
              className="input product-picker-search"
            />
            <div className="product-picker-list">
              {pickerProducts.length === 0 ? (
                <p className="form-help-text">Produktų nerasta arba ieškokite pagal pavadinimą / SKU</p>
              ) : (
                pickerProducts.map(p => (
                  <div key={p.id} className="product-picker-row">
                    <span className="product-picker-name">{p.name}</span>
                    <span className="product-picker-sku">{p.sku || '—'}</span>
                    <span className="product-picker-price">{p.price != null ? parseFloat(p.price).toFixed(2) + ' €' : ''}</span>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleAddRelation(p)}
                    >
                      Pridėti
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Masinio redagavimo modalas
function BulkEditModal({ selectedIds, onClose, onSuccess }) {
  const notification = useNotification();
  const [bulkData, setBulkData] = useState({
    category_id: '',
    is_active: '',
    is_featured: '',
    price_change: '',
    price_change_type: 'set', // 'set' or 'add' or 'percent'
    stock_change: '',
    stock_change_type: 'set'
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await apiClient.get('/categories');
      return res.data;
    },
  });

  const bulkEditMutation = useMutation({
    mutationFn: async (data) => {
      const updates = {};
      
      if (data.category_id) updates.category_id = data.category_id;
      if (data.is_active !== '') updates.is_active = data.is_active === 'true';
      if (data.is_featured !== '') updates.is_featured = data.is_featured === 'true';
      
      // Kainos keitimas
      if (data.price_change) {
        const changeValue = parseFloat(data.price_change);
        if (data.price_change_type === 'set') {
          updates.price = changeValue;
        } else {
          updates.price_change = { type: data.price_change_type, value: changeValue };
        }
      }
      
      // Atsargų keitimas
      if (data.stock_change) {
        const changeValue = parseInt(data.stock_change);
        if (data.stock_change_type === 'set') {
          updates.stock_quantity = changeValue;
        } else {
          updates.stock_change = { type: data.stock_change_type, value: changeValue };
        }
      }

      return apiClient.post('/products/bulk-edit', {
        ids: selectedIds,
        updates
      });
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant produktus');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      notification.warning('Pasirinkite bent vieną produktą');
      return;
    }
    bulkEditMutation.mutate(bulkData);
  };

  return (
    <div className="product-form-overlay" onClick={onClose}>
      <div className="product-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <h2>Masinis redagavimas ({selectedIds.length} produktų)</h2>
          <button onClick={onClose} className="btn-close">×</button>
        </div>
        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-section">
            <h3>Kategorija</h3>
            <select
              value={bulkData.category_id}
              onChange={(e) => setBulkData({ ...bulkData, category_id: e.target.value })}
              className="input"
            >
              <option value="">Nekeisti</option>
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-section">
            <h3>Statusas</h3>
            <div className="form-group">
              <label>Aktyvus</label>
              <select
                value={bulkData.is_active}
                onChange={(e) => setBulkData({ ...bulkData, is_active: e.target.value })}
                className="input"
              >
                <option value="">Nekeisti</option>
                <option value="true">Aktyvuoti</option>
                <option value="false">Deaktyvuoti</option>
              </select>
            </div>
            <div className="form-group">
              <label>Rekomenduojamas</label>
              <select
                value={bulkData.is_featured}
                onChange={(e) => setBulkData({ ...bulkData, is_featured: e.target.value })}
                className="input"
              >
                <option value="">Nekeisti</option>
                <option value="true">Taip</option>
                <option value="false">Ne</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h3>Kaina</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Kainos keitimas</label>
                <input
                  type="number"
                  step="0.01"
                  value={bulkData.price_change}
                  onChange={(e) => setBulkData({ ...bulkData, price_change: e.target.value })}
                  className="input"
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label>Tipas</label>
                <select
                  value={bulkData.price_change_type}
                  onChange={(e) => setBulkData({ ...bulkData, price_change_type: e.target.value })}
                  className="input"
                >
                  <option value="set">Nustatyti</option>
                  <option value="add">Pridėti</option>
                  <option value="percent">Procentais</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Atsargos</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Atsargų keitimas</label>
                <input
                  type="number"
                  value={bulkData.stock_change}
                  onChange={(e) => setBulkData({ ...bulkData, stock_change: e.target.value })}
                  className="input"
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label>Tipas</label>
                <select
                  value={bulkData.stock_change_type}
                  onChange={(e) => setBulkData({ ...bulkData, stock_change_type: e.target.value })}
                  className="input"
                >
                  <option value="set">Nustatyti</option>
                  <option value="add">Pridėti</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Atšaukti
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={bulkEditMutation.isLoading}
            >
              {bulkEditMutation.isLoading ? 'Atnaujinama...' : 'Atnaujinti'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminProducts;
